import { useDispatch, useSelector } from 'react-redux';
import { SelectionToggleType } from '../../enums/SelectionToggleType';
import { ServiceFilterType } from '../../enums/ServiceFilterType';
import { ServicePortfolioType } from '../../enums/ServicePortfolioType';
import { TradebookProviderType } from '../../enums/TradebookProviderType';
import { AppState } from '../../store/AppState';
import {
  setIsServiceFiltersChanged,
  setServiceFilters,
  updateMasterFilterKey,
  updateSelectedFilters,
} from '../../store/service-portfolio/ServiceActions';
import { ServiceState } from '../../store/service-portfolio/ServiceReducer';
import { FilterItemBrandResource, FilterItemResource, MinMaxFilter } from '../../types/FilterTypes';
import {
  appointmentsDefaultDateRangeValue,
  recentsDefaultDateRangeValue,
  ServiceFilterResource,
} from '../../types/service-portfolio/ServiceFilterResource';
import { ServicePortfolioRequestResource } from '../../types/service-portfolio/ServicePortfolioRequestResource';
import { ServiceView } from '../../types/service-portfolio/ServiceView';
import { SortType } from '../../types/SortType';

export const useServicePortfolioFilter = (): PortfolioFilterBoxType => {
  const { driveView, appointmentView, recentView } = useSelector<AppState, ServiceState>(
    (state) => state.service,
  );
  const dispatch = useDispatch();

  const getServiceView = (selectedPortfolioType: ServicePortfolioType): ServiceView => {
    switch (selectedPortfolioType) {
      case ServicePortfolioType.Drive:
        return driveView;
      case ServicePortfolioType.Appointments:
        return appointmentView;
      case ServicePortfolioType.RecentService:
        return recentView;
      default:
        return driveView;
    }
  };

  const updateSingleSelectDropdown = (
    masterKeyData: any,
    data: ServiceFilterResource,
    filterType: ServiceFilterType,
  ) => {
    masterKeyData.forEach((filter) => {
      const currentData = data[filterType].find((x) => x.key === filter.key);
      if (currentData) {
        filter.count = currentData.count;
      }
    });
    data[filterType] = masterKeyData;
    let count = 0;
    data[filterType].forEach((item) => {
      if (item.key.toLowerCase() !== 'all') {
        count += item.count;
      }
    });
    const allOption = data[filterType].find((x) => x.key.toLowerCase() === 'all');
    allOption.count = count;
  };

  const getSelectionToggleType = (
    filter: ServiceFilterType,
    selectedPortfolioType: ServicePortfolioType,
    values: FilterItemResource[],
  ) => {
    const currentView = getServiceView(selectedPortfolioType);
    if (
      !currentView.masterFilterKey ||
      values?.length === 0 ||
      currentView.masterFilterKey === filter
    ) {
      return SelectionToggleType.SelectUnselect;
    } else {
      return SelectionToggleType.Reset;
    }
  };

  const cleanMultiFilters = (
    options: FilterItemResource[],
    selectedOptions: FilterItemResource[],
  ) => {
    selectedOptions = selectedOptions.filter((x) => !!options.find((y) => y.key === x.key));
    return selectedOptions;
  };

  const cleanMultiBrandFilters = (
    options: FilterItemBrandResource[],
    selectedOptions: FilterItemBrandResource[],
  ) => {
    selectedOptions = selectedOptions.filter((x) => !!options.find((y) => y.brandId === x.brandId));
    return selectedOptions;
  };

  const hasFilters = (selectedPortfolioType: ServicePortfolioType): boolean => {
    const currentView = getServiceView(selectedPortfolioType);
    const selectedFilters = currentView.selectedFilters;
    return (
      selectedFilters.dealTypes?.length > 0 ||
      selectedFilters.bpsValue?.min !== undefined ||
      selectedFilters.bpsValue?.max !== undefined ||
      selectedFilters.isTrimConfirmationRequired !== null ||
      selectedFilters.makes.length > 0 ||
      selectedFilters.models.length > 0 ||
      selectedFilters.trims.length > 0 ||
      selectedFilters.years.length > 0 ||
      selectedFilters.salesPersons.length > 0 ||
      selectedFilters.serviceAdvisors.length > 0 ||
      selectedFilters.roStatuses.length > 0 ||
      selectedFilters.interactionStatuses.length > 0 ||
      selectedFilters.optOutPreference.length > 0 ||
      selectedFilters.emailValidationStatus?.key != null ||
      (selectedPortfolioType === ServicePortfolioType.Appointments &&
        JSON.stringify(selectedFilters.dateRange) !==
          JSON.stringify(appointmentsDefaultDateRangeValue)) ||
      (selectedPortfolioType === ServicePortfolioType.RecentService &&
        JSON.stringify(selectedFilters.dateRange) !== JSON.stringify(recentsDefaultDateRangeValue))
    );
  };

  const setSelectedFilter = (
    key: ServiceFilterType,
    data:
      | FilterItemResource
      | FilterItemResource[]
      | MinMaxFilter<number>
      | MinMaxFilter<Date>
      | boolean,
    selectedPortfolioType: ServicePortfolioType,
    triggerSearch: boolean = true,
  ): void => {
    const currentView = getServiceView(selectedPortfolioType);

    const selectedFilters = currentView.selectedFilters;
    const filters = currentView.filters;

    if (triggerSearch) {
      dispatch(setIsServiceFiltersChanged(true, selectedPortfolioType));
    }

    const newSelectedFilters = { ...selectedFilters };
    switch (key) {
      case ServiceFilterType.DealTypes:
      case ServiceFilterType.Models:
      case ServiceFilterType.Trims:
      case ServiceFilterType.Years:
      case ServiceFilterType.SalesPersons:
      case ServiceFilterType.ServiceAdvisors:
      case ServiceFilterType.ROStatuses:
      case ServiceFilterType.InteractionStatuses:
      case ServiceFilterType.OptOutPreference:
        if ((<FilterItemResource[]>data).length > 0 && triggerSearch) {
          dispatch(updateMasterFilterKey(key, selectedPortfolioType));
        }
        newSelectedFilters[key] = cleanMultiFilters(filters[key], <FilterItemResource[]>data);
        dispatch(updateSelectedFilters(newSelectedFilters, selectedPortfolioType));
        break;

      case ServiceFilterType.BpsValue:
      case ServiceFilterType.DateRange:
        dispatch(updateMasterFilterKey(key, selectedPortfolioType));
        newSelectedFilters[key] = <MinMaxFilter<number>>data;
        dispatch(updateSelectedFilters(newSelectedFilters, selectedPortfolioType));
        break;

      case ServiceFilterType.EmailStatus:
        if ((<FilterItemResource>data).key?.toLowerCase() !== 'all') {
          dispatch(updateMasterFilterKey(key, selectedPortfolioType));
        }
        newSelectedFilters[key] = <FilterItemResource>data;
        dispatch(updateSelectedFilters(newSelectedFilters, selectedPortfolioType));
        break;

      case ServiceFilterType.Makes:
        if ((<FilterItemBrandResource[]>data).length > 0 && triggerSearch) {
          dispatch(updateMasterFilterKey(key, selectedPortfolioType));
        }
        newSelectedFilters[key] = cleanMultiBrandFilters(
          filters[key],
          <FilterItemBrandResource[]>data,
        );
        dispatch(updateSelectedFilters(newSelectedFilters, selectedPortfolioType));
        break;

      case ServiceFilterType.IsTrimConfirmationRequired:
        dispatch(updateMasterFilterKey(key, selectedPortfolioType));
        dispatch(
          updateSelectedFilters(
            {
              ...selectedFilters,
              isTrimConfirmationRequired: <boolean>data,
            },
            selectedPortfolioType,
          ),
        );
        break;

      default:
        break;
    }
  };

  const updateWithMasterFilter = (
    data: ServiceFilterResource,
    selectedPortfolioType: ServicePortfolioType,
  ): void => {
    const currentView = getServiceView(selectedPortfolioType);
    const masterFilterKey = currentView.masterFilterKey;
    const filters = currentView.filters;
    let masterKeyData: any;
    if (masterFilterKey) {
      switch (masterFilterKey) {
        case ServiceFilterType.DateRange:
          masterKeyData = <MinMaxFilter<number>>filters[masterFilterKey.valueOf()];
          data[masterFilterKey] = masterKeyData;
          break;

        case ServiceFilterType.IsTrimConfirmationRequired:
          // As this is special case, we need to refresh data as user click on toggle.
          const trims = currentView.selectedFilters.trims;
          const filteredSelectedOptions = trims.filter(
            (x) => !!data.trims.find((y) => y.key === x.key),
          );
          if (filteredSelectedOptions?.length !== trims?.length) {
            setSelectedFilter(
              ServiceFilterType.Trims,
              filteredSelectedOptions,
              selectedPortfolioType,
              false,
            );
          }
          break;

        case ServiceFilterType.BpsValue:
          data.maxBps = filters.maxBps;
          data.minBps = filters.minBps;
          break;

        case ServiceFilterType.EmailStatus:
          masterKeyData = [...filters.emailValidationStatus];
          updateSingleSelectDropdown(masterKeyData, data, ServiceFilterType.EmailStatus);
          break;

        default:
          masterKeyData = [...(<FilterItemResource[]>filters[masterFilterKey.valueOf()])];
          masterKeyData.forEach((filter) => {
            const currentData = data[masterFilterKey].find((x) => x.key === filter.key);
            if (currentData) {
              filter.count = currentData.count;
            }
          });
          const newOptions = (<FilterItemResource[]>data[masterFilterKey]).filter(
            (x) => masterKeyData.findIndex((y) => y.key === x.key) === -1,
          );
          data[masterFilterKey] = [...masterKeyData, ...newOptions];
          break;
      }
    }
    dispatch(setServiceFilters(data, selectedPortfolioType));
  };

  const resetFilters = (selectedPortfolioType: ServicePortfolioType) => {
    dispatch(updateMasterFilterKey(null, selectedPortfolioType));
    dispatch(setIsServiceFiltersChanged(true, selectedPortfolioType));
    dispatch(
      updateSelectedFilters(
        {
          bpsValue: null,
          optOutPreference: [],
          models: [],
          trims: [],
          years: [],
          makes: [],
          isTrimConfirmationRequired: null,
          salesPersons: [],
          serviceAdvisors: [],
          roStatuses: [],
          interactionStatuses: [],
          dealTypes: [],
          dateRange:
            selectedPortfolioType === ServicePortfolioType.Appointments
              ? appointmentsDefaultDateRangeValue
              : recentsDefaultDateRangeValue,
          emailValidationStatus: null,
        },
        selectedPortfolioType,
      ),
    );
  };

  const getRequestResourceByPortfolioType = (
    postData: ServicePortfolioRequestResource,
    selectedPortfolioType: ServicePortfolioType,
    previousPortfolioType: ServicePortfolioType,
    setSearchText: (searchText: string) => void,
  ): ServicePortfolioRequestResource => {
    let currentPortfolioTypeRequest: ServicePortfolioRequestResource;

    if (previousPortfolioType !== selectedPortfolioType) {
      switch (selectedPortfolioType) {
        case ServicePortfolioType.Drive:
          if (driveView.serviceRequest !== null) {
            currentPortfolioTypeRequest = {
              ...postData,
              ...driveView.serviceRequest,
            };
            setSearchText(currentPortfolioTypeRequest.search);
          } else {
            setSearchText('');
            currentPortfolioTypeRequest = postData;
            currentPortfolioTypeRequest.search = '';
          }
          break;

        case ServicePortfolioType.Appointments:
          if (appointmentView.serviceRequest !== null) {
            currentPortfolioTypeRequest = {
              ...postData,
              ...appointmentView.serviceRequest,
            };
            setSearchText(currentPortfolioTypeRequest.search);
          } else {
            setSearchText('');
            currentPortfolioTypeRequest = postData;
            currentPortfolioTypeRequest.search = '';
          }
          break;

        case ServicePortfolioType.RecentService:
          if (recentView.serviceRequest !== null) {
            currentPortfolioTypeRequest = {
              ...postData,
              ...recentView.serviceRequest,
            };
            setSearchText(currentPortfolioTypeRequest.search);
          } else {
            setSearchText('');
            currentPortfolioTypeRequest = postData;
            currentPortfolioTypeRequest.search = '';
          }
          break;
      }
    } else {
      currentPortfolioTypeRequest = postData;
    }

    return currentPortfolioTypeRequest;
  };

  const getPortfolioRequestResource = (
    sort: SortType,
    searchText: string,
    pageSize: number,
    currentPage: number,
    selectedPortfolioType: ServicePortfolioType,
    previousPortfolioType: ServicePortfolioType,
    setSearchText: (searchText: string) => void,
  ): ServicePortfolioRequestResource => {
    const currentView = getServiceView(selectedPortfolioType);

    const selectedFilters = currentView.selectedFilters;
    const postData: ServicePortfolioRequestResource = {
      currentPage: currentPage,
      recordsPerPage: pageSize,
      search: searchText,
      sort: sort.accessor,
      sortDirection: sort.sortDirection,
      subSort: sort.subSort,
      subSortDirection: sort.subSortDirection,
      bps: {
        min: selectedFilters.bpsValue && selectedFilters.bpsValue?.min,
        max:
          selectedFilters.bpsValue && selectedFilters.bpsValue.max
            ? selectedFilters.bpsValue.max
            : null,
      },
      dateRange: {
        min:
          selectedFilters.dateRange && selectedFilters.dateRange.min !== null
            ? selectedPortfolioType === ServicePortfolioType.RecentService
              ? selectedFilters.dateRange?.min
              : selectedFilters.dateRange?.min - 1
            : null,
        max:
          selectedFilters.dateRange && selectedFilters.dateRange.max != null
            ? selectedFilters.dateRange.max
            : null,
      },
      salesPersons:
        selectedFilters.salesPersons && selectedFilters.salesPersons.length
          ? selectedFilters.salesPersons.map((x) => x.key)
          : null,
      serviceAdvisors:
        selectedFilters.serviceAdvisors && selectedFilters.serviceAdvisors.length
          ? selectedFilters.serviceAdvisors.map((x) => x.key)
          : null,
      roStatuses:
        selectedFilters.roStatuses && selectedFilters.roStatuses.length
          ? selectedFilters.roStatuses.map((x) => x.key)
          : null,
      dealTypes:
        selectedFilters.dealTypes && selectedFilters.dealTypes.length
          ? selectedFilters.dealTypes.map((x) => parseInt(x.key))
          : [],
      interactionStatuses:
        selectedFilters.interactionStatuses && selectedFilters.interactionStatuses.length
          ? selectedFilters.interactionStatuses.map((x) => x.key)
          : null,
      trims:
        selectedFilters.trims && selectedFilters.trims.length
          ? selectedFilters.trims.map((x) => x.key)
          : null,
      isTrimConfirmationRequired: selectedFilters.isTrimConfirmationRequired,
      models:
        selectedFilters.models && selectedFilters.models.length
          ? selectedFilters.models.map((x) => x.key)
          : null,
      years:
        selectedFilters.years && selectedFilters.years.length
          ? selectedFilters.years.map((x) => x.key)
          : null,
      makes:
        selectedFilters.makes && selectedFilters.makes.length
          ? Array.from(
              new Set(
                [].concat.apply(
                  [],
                  selectedFilters.makes.map((x) => x.dealerIds),
                ),
              ),
            )
          : null,
      brandIds:
        selectedFilters.makes && selectedFilters.makes.length
          ? selectedFilters.makes.map((x) => x.brandId)
          : null,
      emailValidationStatus:
        selectedFilters.emailValidationStatus &&
        selectedFilters?.emailValidationStatus?.key.toLocaleLowerCase() !== 'all'
          ? parseInt(selectedFilters.emailValidationStatus.key)
          : null,
      optOutPreference: selectedFilters.optOutPreference?.length
        ? selectedFilters.optOutPreference.map((x) => x.key)
        : null,
    };

    return getRequestResourceByPortfolioType(
      postData,
      selectedPortfolioType,
      previousPortfolioType,
      setSearchText,
    );
  };

  const getTradeProviderName = (tradeValue?: number): string => {
    return tradeValue === 2 ? 'kbb' : TradebookProviderType[tradeValue]?.toLocaleLowerCase();
  };

  return {
    getServiceView,
    setSelectedFilter,
    getSelectionToggleType,
    resetFilters,
    updateWithMasterFilter,
    getPortfolioRequestResource,
    hasFilters,
    getTradeProviderName,
  };
};

export type PortfolioFilterBoxType = {
  getServiceView: (selectedPortfolioType: ServicePortfolioType) => ServiceView;
  setSelectedFilter: (
    key: ServiceFilterType,
    data:
      | FilterItemResource
      | FilterItemResource[]
      | MinMaxFilter<number>
      | MinMaxFilter<Date>
      | boolean,
    selectedPortfolioType: ServicePortfolioType,
    triggerSearch?: boolean,
  ) => void;
  getSelectionToggleType: (
    filter: ServiceFilterType,
    selectedPortfolioType: ServicePortfolioType,
    values: FilterItemResource[],
  ) => SelectionToggleType;
  resetFilters: (selectedPortfolioType: ServicePortfolioType) => void;
  updateWithMasterFilter: (
    data: ServiceFilterResource,
    selectedPortfolioType: ServicePortfolioType,
  ) => void;
  getPortfolioRequestResource: (
    sort: SortType,
    searchText: string,
    pageSize: number,
    currentPage: number,
    selectedPortfolioType: string,
    previousPortfolioType: string,
    setSearchText: (searchText: string) => void,
  ) => ServicePortfolioRequestResource;
  hasFilters: (selectedPortfolioType: ServicePortfolioType) => boolean;
  getTradeProviderName: (tradeValue?: number) => string;
};
